import styled from '@emotion/styled';
import {
  Circle,
  CircleHelp,
  Copy,
  Diamond,
  Grid2x2,
  Grid3x3,
  Group,
  Hexagon,
  Image as ImageIcon,
  LayoutTemplate,
  Maximize,
  Pentagon,
  Redo2,
  Shapes,
  Square,
  Star,
  TextCursorInput,
  Trash2,
  Triangle,
  TriangleRight,
  Type,
  Undo2,
  Ungroup,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { FabricPluginName, type FabricEditor, useFabricEditor } from 'modules/fabric-editor';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import UDIconButton from 'ud-ui/components/icon-button';
import UDTippy, { UDTippyProps } from 'ud-ui/components/tippy';
import { LayoutBlocksModal } from '../layout-blocks-modal';
import { theme } from 'styles/theme/theme';

// общие пропсы всех кнопок тулбара — чтобы вариант и размер не разъезжались между ними
const buttonProps = { variant: 'ghost', size: 'md' } as const;

// тип инстанса берём из пропсов UDTippy, чтобы не тянуть транзитивный пакет tippy.js
type TippyInstance = Parameters<NonNullable<UDTippyProps['onCreate']>>[0];

interface ToolButtonProps {
  label: string;
  icon: JSX.Element;
  onClick: () => void;
  disabled?: boolean;
  /** Куда показывать подсказку: в колонке тулбара — справа, в поповере фигур — сверху. */
  tooltipPlacement?: UDTippyProps['placement'];
}

// подсказка вешается снаружи, а не через проп tooltip: у UDTippy здесь свои size/placement.
// span нужен, чтобы подсказка работала и на disabled-кнопке — та не отдаёт mouse-события
const ToolButton: React.FC<ToolButtonProps> = ({ label, icon, onClick, disabled, tooltipPlacement = 'right' }) => (
  <UDTippy content={label} size="xs" placement={tooltipPlacement}>
    <span style={{ display: 'inline-block' }}>
      <UDIconButton {...buttonProps} aria-label={label} icon={icon} onClick={onClick} disabled={disabled} />
    </span>
  </UDTippy>
);

interface PopoverToolButtonProps {
  label: string;
  icon: JSX.Element;
  /** Содержимое поповера; `close` закрывает его — например после выбора фигуры. */
  popover: (close: () => void) => React.ReactNode;
}

/**
 * Кнопка тулбара, у которой по клику раскрывается поповер. На кнопке два тултипа: поповер по
 * клику (висит на span, чтобы клик по кнопке до него всплыл) и hover-подсказка с названием
 * (висит на самой кнопке) — иначе такая кнопка остаётся единственной в тулбаре без объяснения,
 * что она делает. Пока поповер открыт, подсказка отключена: оба раскрываются справа от кнопки
 * и иначе перекрывали бы друг друга.
 */
const PopoverToolButton: React.FC<PopoverToolButtonProps> = ({ label, icon, popover }) => {
  const instance = useRef<TippyInstance | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <UDTippy
      content={popover(() => instance.current?.hide())}
      size="xs"
      placement="right"
      trigger="click"
      interactive
      onCreate={(created) => {
        instance.current = created;
      }}
      onShow={() => setIsOpen(true)}
      onHide={() => setIsOpen(false)}
    >
      <span style={{ display: 'inline-block' }}>
        <UDTippy content={label} size="xs" placement="right" disabled={isOpen}>
          <UDIconButton {...buttonProps} aria-label={label} icon={icon} />
        </UDTippy>
      </span>
    </UDTippy>
  );
};

// Mirrors the real key bindings: hotkey-plugin defaults (undo/redo/delete/copy/paste),
// media-manager-editor-page (layer + align/distribute), use-style-clipboard-hotkeys
// (copy/paste style) and use-save-hotkey (save). Keep in sync if those change.
const SHORTCUTS: Array<{ title: string; items: Array<[label: string, keys: string]> }> = [
  {
    title: 'Правка',
    items: [
      ['Отменить', 'Ctrl/⌘ + Z'],
      ['Повторить', 'Ctrl/⌘ + Shift + Z'],
      ['Удалить', 'Delete / Backspace'],
      ['Копировать', 'Ctrl/⌘ + C'],
      ['Вставить', 'Ctrl/⌘ + V'],
      ['Сохранить', 'Ctrl/⌘ + S'],
    ],
  },
  {
    title: 'Стиль',
    items: [
      ['Скопировать стиль', 'Ctrl/⌘ + Shift + C'],
      ['Вставить стиль', 'Ctrl/⌘ + Shift + V'],
    ],
  },
  {
    title: 'Слой',
    items: [
      ['На передний план (шаг)', 'Ctrl/⌘ + ]'],
      ['На самый верх', 'Ctrl/⌘ + Shift + ]'],
      ['На задний план (шаг)', 'Ctrl/⌘ + ['],
      ['В самый низ', 'Ctrl/⌘ + Shift + ['],
    ],
  },
  {
    title: 'Выравнивание',
    items: [
      ['Выровнять влево / вправо', 'Alt + A / D'],
      ['Выровнять сверху / снизу', 'Alt + W / S'],
      ['Распределить гор. / верт.', 'Alt + H / V'],
    ],
  },
  {
    title: 'Сетка',
    items: [['Подогнать по сетке', 'Alt + G']],
  },
];

const ShortcutsHelp: React.FC = () => (
  <Shortcuts>
    {SHORTCUTS.map((group) => (
      <ShortcutGroup key={group.title}>
        <ShortcutGroupTitle>{group.title}</ShortcutGroupTitle>
        {group.items.map(([label, keys]) => (
          <ShortcutRow key={label}>
            <span>{label}</span>
            <ShortcutKeys>{keys}</ShortcutKeys>
          </ShortcutRow>
        ))}
      </ShortcutGroup>
    ))}
  </Shortcuts>
);

// Pure geometry — vertices for the regular/star polygons the Shapes menu builds.
// Kept here (not in fabric-editor) because they are plain {x,y} data the
// `editor.objects.addPolygon` façade consumes; no Fabric value is touched.
const polygonPoints = (sides: number, radius: number, cx: number, cy: number, offsetAngle = 0) =>
  Array.from({ length: sides }, (_, i) => {
    const angle = (i * 2 * Math.PI) / sides + offsetAngle;
    return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  });

const starPoints = (outerR: number, innerR: number, cx: number, cy: number) =>
  Array.from({ length: 10 }, (_, i) => {
    const angle = (i * Math.PI) / 5 - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
  });

const SHAPES: Array<{ label: string; icon: JSX.Element; add: (editor: FabricEditor) => void }> = [
  {
    label: 'Прямоугольник',
    icon: <Square strokeWidth={1.5} />,
    add: (e) => e.objects.addRect({ width: 200, height: 100 }),
  },
  { label: 'Круг', icon: <Circle strokeWidth={1.5} />, add: (e) => e.objects.addCircle({ radius: 100 }) },
  {
    label: 'Треугольник',
    icon: <Triangle strokeWidth={1.5} />,
    add: (e) => e.objects.addTriangle({ width: 200, height: 173 }),
  },
  {
    label: 'Прямоугольный треугольник',
    icon: <TriangleRight strokeWidth={1.5} />,
    add: (e) =>
      e.objects.addPolygon({
        points: [
          { x: 0, y: 0 },
          { x: 0, y: 200 },
          { x: 200, y: 200 },
        ],
      }),
  },
  {
    label: 'Пятиугольник',
    icon: <Pentagon strokeWidth={1.5} />,
    add: (e) => e.objects.addPolygon({ points: polygonPoints(5, 100, 100, 100, -Math.PI / 2) }),
  },
  {
    label: 'Шестиугольник',
    icon: <Hexagon strokeWidth={1.5} />,
    add: (e) => e.objects.addPolygon({ points: polygonPoints(6, 100, 100, 100) }),
  },
  {
    label: 'Звезда',
    icon: <Star strokeWidth={1.5} />,
    add: (e) => e.objects.addPolygon({ points: starPoints(100, 45, 100, 100) }),
  },
  {
    label: 'Ромб',
    icon: <Diamond strokeWidth={1.5} />,
    add: (e) =>
      e.objects.addPolygon({
        points: [
          { x: 100, y: 0 },
          { x: 200, y: 100 },
          { x: 100, y: 200 },
          { x: 0, y: 100 },
        ],
      }),
  },
];

// onPick закрывает поповер: выбор фигуры — однократное действие, держать меню открытым не нужно
const ShapesMenu: React.FC<{ editor: FabricEditor; onPick: () => void }> = ({ editor, onPick }) => (
  <ShapesGrid>
    {SHAPES.map((shape) => (
      <ToolButton
        key={shape.label}
        label={shape.label}
        icon={shape.icon}
        tooltipPlacement="top"
        onClick={() => {
          shape.add(editor);
          onPick();
        }}
      />
    ))}
  </ShapesGrid>
);

const EditorLeftToolbar: React.FC = () => {
  const editor = useFabricEditor();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  // 'group' = a multi-selection that can be grouped; 'ungroup' = a single group
  // that can be split; 'none' = neither (button disabled).
  const [groupState, setGroupState] = useState<'group' | 'ungroup' | 'none'>('none');
  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const layoutPresets = useMemo(() => editor.layoutBlocks?.listPresets() ?? [], [editor]);

  useEffect(() => {
    const off = editor.on('history:changed', (e) => {
      setCanUndo(e.canUndo);
      setCanRedo(e.canRedo);
    });
    return off;
  }, [editor]);

  useEffect(() => {
    const off = editor.on('selection:changed', (e) => {
      setHasSelection(e.active.length > 0);
      // A multi-selection (ActiveSelection) reports its members; a lone group
      // reports just itself. Drives the Group/Ungroup toggle.
      if (e.active.length >= 2) setGroupState('group');
      else if (e.active.length === 1 && e.active[0]?.type === 'group') setGroupState('ungroup');
      else setGroupState('none');
    });
    return off;
  }, [editor]);

  const deleteSelected = useCallback(() => {
    const selected = editor.selection?.getActive() ?? [];
    if (selected.length === 0) return;
    // batch() collapses a multi-object delete into a single undo step.
    editor.history?.batch(() => {
      for (const obj of selected) editor.objects.remove(obj);
    });
    editor.selection?.clear();
  }, [editor]);

  const addText = useCallback(() => {
    editor.objects.addText({ text: 'Текст', fontSize: 48 });
  }, [editor]);

  const addImagePlaceholder = useCallback(() => {
    editor.placeholders?.addImage({
      placeholderKey: '',
      placeholderLabel: 'Плейсхолдер изображения',
      width: 320,
      height: 240,
    });
  }, [editor]);

  const addTextPlaceholder = useCallback(() => {
    editor.placeholders?.addText({
      placeholderKey: '',
      placeholderLabel: 'Текстовый плейсхолдер',
      sampleText: 'Образец текста',
      width: 400,
      height: 100,
    });
  }, [editor]);

  const upGrid = useCallback(() => {
    const hasGrid = editor.hasPlugin(FabricPluginName.GRID);
    if (!hasGrid || !editor.grid) throw new Error('Grid plugin is not available');

    const currentStep = editor.grid.getStep();
    editor.grid.setStep(currentStep + 1);
  }, [editor]);

  const downGrid = useCallback(() => {
    const hasGrid = editor.hasPlugin(FabricPluginName.GRID);
    if (!hasGrid || !editor.grid) throw new Error('Grid plugin is not available');

    const currentStep = editor.grid.getStep();
    editor.grid.setStep(currentStep - 1);
  }, [editor]);

  return (
    <Root>
      <ToolButton label="Текст" icon={<Type strokeWidth={1.5} />} onClick={addText} />
      <PopoverToolButton
        label="Фигуры"
        icon={<Shapes strokeWidth={1.5} />}
        popover={(close) => <ShapesMenu editor={editor} onPick={close} />}
      />

      <Divider />

      <ToolButton label="Плейсхолдер изображения" icon={<ImageIcon strokeWidth={1.5} />} onClick={addImagePlaceholder} />
      <ToolButton label="Текстовый плейсхолдер" icon={<TextCursorInput strokeWidth={1.5} />} onClick={addTextPlaceholder} />

      <Divider />

      <ToolButton label="Блоки макета" icon={<LayoutTemplate strokeWidth={1.5} />} onClick={() => setShowLayoutModal(true)} />

      <Divider />

      <ToolButton label="Отменить" icon={<Undo2 strokeWidth={1.5} />} onClick={() => editor.history?.undo()} disabled={!canUndo} />
      <ToolButton label="Повторить" icon={<Redo2 strokeWidth={1.5} />} onClick={() => editor.history?.redo()} disabled={!canRedo} />

      <Divider />

      <ToolButton label="Приблизить" icon={<ZoomIn strokeWidth={1.5} />} onClick={() => editor.zoom?.in()} />
      <ToolButton label="Отдалить" icon={<ZoomOut strokeWidth={1.5} />} onClick={() => editor.zoom?.out()} />
      <ToolButton label="Сбросить масштаб" icon={<Maximize strokeWidth={1.5} />} onClick={() => editor.zoom?.reset()} />

      <Divider />

      <ToolButton label="Увеличить шаг сетки" icon={<Grid3x3 strokeWidth={1.5} />} onClick={upGrid} />
      <ToolButton label="Уменьшить шаг сетки" icon={<Grid2x2 strokeWidth={1.5} />} onClick={downGrid} />

      <Divider />

      <ToolButton
        label="Дублировать"
        icon={<Copy strokeWidth={1.5} />}
        onClick={() => editor.objects.duplicate()}
        disabled={!hasSelection}
      />
      <ToolButton
        label={groupState === 'ungroup' ? 'Разгруппировать' : 'Сгруппировать'}
        icon={groupState === 'ungroup' ? <Ungroup strokeWidth={1.5} /> : <Group strokeWidth={1.5} />}
        onClick={() => (groupState === 'ungroup' ? editor.objects.ungroup() : editor.objects.group())}
        disabled={groupState === 'none'}
      />
      <ToolButton label="Удалить" icon={<Trash2 strokeWidth={1.5} />} onClick={deleteSelected} disabled={!hasSelection} />
      <PopoverToolButton label="Горячие клавиши" icon={<CircleHelp strokeWidth={1.5} />} popover={() => <ShortcutsHelp />} />

      <LayoutBlocksModal
        show={showLayoutModal}
        onClose={() => setShowLayoutModal(false)}
        presets={layoutPresets}
        currentPresetId={editor.layoutBlocks?.getData()?.presetId}
        onPick={(id) => editor.layoutBlocks?.apply(id)}
      />
    </Root>
  );
};

export default React.memo(EditorLeftToolbar);

const Root = styled.div`
  width: 52px;
  height: 100%;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border-right: 1px solid ${theme.colors.DarkBlue100};
  background: ${theme.colors.SurfaceWhite};
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${theme.colors.DarkBlue100};
  margin: 4px 0;
`;

const ShapesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
`;

const Shortcuts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 200px;
  padding: 2px;
`;

const ShortcutGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ShortcutGroupTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${theme.colors.Gray};
`;

const ShortcutRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-size: 12px;
  color: ${theme.colors.DarkBlue600};
`;

const ShortcutKeys = styled.kbd`
  font-family: inherit;
  font-size: 11px;
  color: ${theme.colors.DarkBlue400};
  background: ${theme.colors.SurfaceCultured10};
  border: 1px solid ${theme.colors.DarkBlue100};
  border-radius: 4px;
  padding: 1px 6px;
  white-space: nowrap;
`;
